import { motion } from "framer-motion";
import { Check, X, Shield, ArrowRight, Lock, FileCheck } from "lucide-react";

export const EcosystemDiagram = () => {
  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 bg-radial-glow opacity-30" />
      
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
            Independent technical verification
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            PROVENANCE sits between buyers and sellers to surface truth without exposing IP
          </p>
        </motion.div>

        {/* Main Diagram */}
        <div className="relative max-w-5xl mx-auto">
          {/* Connection Lines - SVG */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 1000 400">
            {/* Left to Center */}
            <motion.path
              d="M 200 200 L 400 200"
              stroke="url(#gradientLine)"
              strokeWidth="2"
              fill="none"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.5 }}
            />
            {/* Center to Right */}
            <motion.path
              d="M 600 200 L 800 200"
              stroke="url(#gradientLine)"
              strokeWidth="2"
              fill="none"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.7 }}
            />
            {/* Arrow heads */}
            <defs>
              <linearGradient id="gradientLine" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="hsl(174, 100%, 50%)" />
                <stop offset="100%" stopColor="hsl(199, 100%, 50%)" />
              </linearGradient>
            </defs>
          </svg>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center relative z-10">
            {/* Seller Side */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-surface-dark border border-border rounded-2xl p-6"
            >
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 border border-primary/20 mb-3">
                  <Lock className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-display font-bold text-xl">SELLER</h3>
              </div>
              
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-primary mb-2">Provides:</p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-score-good" />
                      <span className="text-muted-foreground">Source code & artefacts</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-score-good" />
                      <span className="text-muted-foreground">Read-only endpoints</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-score-warning mb-2">Sees:</p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <X className="h-4 w-4 text-score-warning" />
                      <span className="text-muted-foreground">Ingestion status only</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <X className="h-4 w-4 text-score-warning" />
                      <span className="text-muted-foreground">No findings, no scores</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Flow Arrow */}
              <div className="hidden md:flex items-center justify-end mt-6 text-xs text-muted-foreground">
                <span className="mr-2">Artefacts</span>
                <ArrowRight className="h-4 w-4 text-primary" />
              </div>
            </motion.div>

            {/* Center - PROVENANCE */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="relative"
            >
              {/* Glow effect */}
              <div className="absolute inset-0 rounded-full bg-primary/20 blur-3xl" />
              
              <div className="relative bg-gradient-to-b from-surface-elevated to-background border-2 border-primary rounded-full aspect-square flex flex-col items-center justify-center p-8 glow-primary">
                <Shield className="h-10 w-10 text-primary mb-3" />
                <h3 className="font-display font-bold text-2xl text-gradient">PROVENANCE</h3>
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Independent Verification Layer
                </p>
                
                <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap">
                  <p className="text-xs text-muted-foreground">
                    <span className="text-primary">No IP transfer</span> • <span className="text-primary">No trust required</span>
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Buyer Side */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-surface-dark border border-border rounded-2xl p-6"
            >
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 border border-primary/20 mb-3">
                  <FileCheck className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-display font-bold text-xl">BUYER / ACQUIRER</h3>
              </div>
              
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-primary mb-2">Receives:</p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-score-good" />
                      <span className="text-muted-foreground">Technology Reality Score™</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-score-good" />
                      <span className="text-muted-foreground">Pillar breakdown</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-score-good" />
                      <span className="text-muted-foreground">Key risks & remediation</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-score-critical mb-2">Never receives:</p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <X className="h-4 w-4 text-score-critical" />
                      <span className="text-muted-foreground">Raw code</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <X className="h-4 w-4 text-score-critical" />
                      <span className="text-muted-foreground">Logs or artefacts</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Flow Arrow */}
              <div className="hidden md:flex items-center justify-start mt-6 text-xs text-muted-foreground">
                <ArrowRight className="h-4 w-4 text-primary rotate-180 mr-2" />
                <span>Verified conclusions</span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
